// Facebook (Meta) write-back: overlay a review grid's pending scalar edits and
// list operations onto a launcher result (the campaign config the campaign
// launcher wrote to parsed_response.json).
//
// ONE module owns every Facebook write, reached through
// ../reviewEdits.js#applyLauncherResultEdits (CLW review pane +
// LaunchCampaignDetails), which owns the deep clone. The helpers here mutate
// that clone in place, exactly like the TTD / DV360 / Google Ads appliers next
// to them.
//
// THE WRITE TARGET IS WHAT THE ROW BUILDER READS (./fbRowBuilders.js) — every
// writer below targets the exact key buildFbReviewRows renders, so a saved edit
// re-renders as typed.
//
// Row addressing: a Facebook plan carries no entity ids before launch, so
// buildFbReviewRows assigns positional rowIds — `fb-{adSetIdx}` — which are
// stable because the builder walks `ad_sets` in order.
//
// SCOPE: Facebook states the objective, attribution and (on a CBO plan) the
// budget on the CAMPAIGN. Those cells are editable and their write is
// campaign-scoped by construction — every sibling row re-reads the new value.
// The `campaignBudget` cell shows the campaign total's per-ad-set share, so
// that one write scales back up (share × ad sets), the same contract TTD's and
// Google Ads' budget writers use.
//
// UNITS: whole currency units, NOT cents. fb_campaign_preprocessor converts to
// cents at launch (_convert_budget × 100), so writing cents here would inflate
// every budget a hundredfold the moment the plan is launched.

import {
  fbAdSetsKey,
  fbCreativesOf,
  fbTargetingOf,
} from './fbRowBuilders';
import { numeric, scaleToParent, normalizeRowOps } from '../sharedFieldWriters';
import { applyFbRowOps } from '../rowOps';

const asArray = (v) => (Array.isArray(v) ? v : []);
const nonEmpty = (v) => (v == null ? null : (String(v).trim() || null));

// ' · '-joined list cell → its entries (the separator the row builders join
// list cells with).
const splitListDraft = (draft) => String(draft || '')
  .split('·').map((s) => s.trim()).filter((s) => s && s !== '—');

// Comma-separated cell (languages, devices) → trimmed entries.
const splitCommaDraft = (draft) => String(draft || '')
  .split(',').map((s) => s.trim()).filter((s) => s && s !== '—');

// 'YYYY-MM-DD' — the native date editor's output. Anything else is a no-op
// (null), so a half-typed date never lands on the plan.
const ymd = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/** Parse `fb-{adSetIdx}` → the index, or null when it isn't one. */
export function parseFbRowId(rowId) {
  const m = /^fb-(\d+)$/.exec(String(rowId || ''));
  return m ? { adSetIdx: Number(m[1]) } : null;
}

/**
 * Resolve a row to its ad set plus the campaign envelope. The campaign object
 * is CREATED when the plan states none, so a campaign-scoped edit on a
 * campaign-less plan lands somewhere rather than being silently dropped.
 */
function resolveTargets(edited, rowId) {
  const at = parseFbRowId(rowId);
  if (!at) return null;
  const adSets = asArray(edited[fbAdSetsKey(edited)]);
  const adSet = adSets[at.adSetIdx];
  if (!adSet) return null;
  if (!edited.campaign || typeof edited.campaign !== 'object' || Array.isArray(edited.campaign)) {
    edited.campaign = {};
  }
  return { campaign: edited.campaign, adSet, adSetCount: adSets.length || 1 };
}

/**
 * The targeting object an ad set's edits should write to.
 *
 * fbTargetingOf READS through `targeting_profiles` as well as inline
 * `targeting`, but a write must never reach into a shared profile — profiles
 * are referenced by several ad sets, so editing one row's geo would silently
 * retarget its siblings. Writes therefore always land on the ad set's OWN
 * inline targeting, which the validator treats as the ad set's authority
 * (fb_campaign_validator._validate_adsets checks inline targeting first).
 *
 * When the ad set has no inline targeting yet, the profile-resolved values are
 * COPIED down first, so a row that renders "US · Canada" and has one entry
 * deleted keeps the other rather than starting from an empty object.
 */
function writableTargeting(adSet, result) {
  if (adSet.targeting && typeof adSet.targeting === 'object' && !Array.isArray(adSet.targeting)) {
    return adSet.targeting;
  }
  const resolved = fbTargetingOf(adSet, result);
  // Deep clone so the copied-down values can't alias the shared profile.
  adSet.targeting = JSON.parse(JSON.stringify(resolved || {}));
  return adSet.targeting;
}

/** The geo_locations bag on a targeting object, created on demand. */
function geoBag(targeting, key = 'geo_locations') {
  if (!targeting[key] || typeof targeting[key] !== 'object' || Array.isArray(targeting[key])) {
    targeting[key] = {};
  }
  return targeting[key];
}

// ── Scalar edits ─────────────────────────────────────────────────────

// Campaign-scoped cells. Every row of the plan re-reads these — Facebook's
// entity model puts them on the campaign, so there is nowhere else to write.
function applyFbCampaignScoped(campaign, fields, adSetCount) {
  if (fields.market != null && String(fields.market).trim()) {
    // The row builder reads campaign_name first, then name — write whichever
    // the plan already carries so read and write stay on one key.
    if (campaign.campaign_name != null || campaign.name == null) {
      campaign.campaign_name = String(fields.market).trim();
    } else {
      campaign.name = String(fields.market).trim();
    }
  }
  if (fields.objective != null && String(fields.objective).trim()) {
    campaign.goal = String(fields.objective).trim();
  }
  if (fields.attribution != null && String(fields.attribution).trim()) {
    campaign.attribution_setting = String(fields.attribution).trim();
  }
  if (fields.campaignBudget != null) {
    // Through the shared scaleToParent so FB_SCALED_FIELDS' pre-commit preview
    // derives the identical total.
    const total = scaleToParent(numeric(fields.campaignBudget), adSetCount);
    if (total != null) {
      // Write the key the plan already uses; `budget` is the launch template's
      // own spelling and lifetime_budget the preprocessor's.
      if (campaign.lifetime_budget != null) campaign.lifetime_budget = total;
      else campaign.budget = total;
    }
  }
}

// Ad-set-scoped scalar cells.
function applyFbAdSetFields(adSet, fields) {
  if (fields.name != null && String(fields.name).trim()) {
    adSet.name = String(fields.name).trim();
  }
  if (fields.package != null && String(fields.package).trim()) {
    adSet.package_name = String(fields.package).trim();
  }
  if (fields.category != null && String(fields.category).trim()) {
    adSet.ad_set_suffix = String(fields.category).trim();
  }
  if (fields.tactic != null && String(fields.tactic).trim()) {
    // The validator lower-cases before matching VALID_TACTICS, so normalise
    // here rather than letting "Prospecting" fail validation at launch.
    adSet.tactic = String(fields.tactic).trim().toLowerCase();
  }
  if (fields.start != null) {
    const v = ymd(fields.start);
    if (v) adSet.start_date = v;
  }
  if (fields.end != null) {
    const v = ymd(fields.end);
    if (v) adSet.end_date = v;
  }
  if (fields.budget != null) {
    const n = numeric(fields.budget);
    // Ad-set budgets are stated per ad set (ABO) — written as typed, no scaling.
    if (n != null) {
      if (adSet.lifetime_budget != null) adSet.lifetime_budget = n;
      else adSet.budget = n;
    }
  }
  if (fields.dailyBudget != null) {
    const n = numeric(fields.dailyBudget);
    if (n != null) adSet.daily_budget = n;
  }
  if (fields.bidFloor != null) {
    const n = numeric(fields.bidFloor);
    if (n != null) adSet.bid_amount = n;
  }
  if (fields.bidStrategy != null && String(fields.bidStrategy).trim()) {
    // Meta's bid strategies are upper-snake enums (LOWEST_COST_WITHOUT_CAP);
    // the validator rejects anything else, so normalise the casing a reviewer
    // is likely to type.
    adSet.bid_strategy = String(fields.bidStrategy).trim().toUpperCase().replace(/\s+/g, '_');
  }
  if (fields.optimizationGoal != null && String(fields.optimizationGoal).trim()) {
    adSet.optimization_goal = String(fields.optimizationGoal).trim().toUpperCase().replace(/\s+/g, '_');
  }
  if (fields.billingEvent != null && String(fields.billingEvent).trim()) {
    adSet.billing_event = String(fields.billingEvent).trim().toUpperCase().replace(/\s+/g, '_');
  }
  if (fields.pacing != null) applyFbPacing(adSet, fields.pacing);
  // NO entityStatus WRITE. `entityStatus` is denylisted (FB_DENYLIST) and
  // refused here too, because the denylist is not the only route into this
  // applier — LaunchCampaignDetails' applyFlagSuggestion stages edits by field
  // name without consulting the editable set. Activation is the launch action's
  // job, not a cell; the same rule holds on every other platform.
}

// 'Daily budget' / 'Lifetime budget' — the pacing cell's own rendering. The
// two are mutually exclusive on Meta, so switching moves the money rather than
// leaving both keys set (which the API rejects).
function applyFbPacing(adSet, draft) {
  const s = String(draft).trim().toLowerCase();
  if (!s) return;
  if (s.includes('daily')) {
    if (adSet.daily_budget == null) {
      const lifetime = adSet.lifetime_budget ?? adSet.budget;
      if (lifetime != null) adSet.daily_budget = lifetime;
    }
    delete adSet.lifetime_budget;
    delete adSet.budget;
  } else if (s.includes('lifetime')) {
    if (adSet.lifetime_budget == null && adSet.budget == null && adSet.daily_budget != null) {
      adSet.budget = adSet.daily_budget;
    }
    delete adSet.daily_budget;
  }
}

// Targeting scalars that render as comma / range strings rather than lists.
function applyFbTargetingFields(targeting, fields) {
  if (fields.age != null) applyFbAge(targeting, fields.age);
  if (fields.gender != null) applyFbGender(targeting, fields.gender);
  if (fields.languages != null) {
    targeting.locales = splitCommaDraft(fields.languages);
  }
  if (fields.devices != null) {
    const list = splitCommaDraft(fields.devices)
      .filter((d) => d.toLowerCase() !== 'all devices')
      .map((d) => d.toLowerCase());
    // "All devices" is the cell's rendering of NO device targeting, so clearing
    // back to it must remove the key — an empty array is a different thing to
    // Meta (it rejects one).
    if (list.length) targeting.device_platforms = list;
    else delete targeting.device_platforms;
  }
}

// "25–54" / "25-54" / "25+" / "25" — the age cell's own vocabulary. Meta clamps
// to 18–65, and the validator enforces it, so an out-of-range draft is refused
// here rather than failing at launch.
function applyFbAge(targeting, draft) {
  const s = String(draft).trim();
  if (!s) return;
  const inRange = (n) => Number.isFinite(n) && n >= 18 && n <= 65;
  const range = /^(\d{2})\s*[–—-]\s*(\d{2})$/.exec(s);
  if (range) {
    const min = Number(range[1]);
    const max = Number(range[2]);
    if (inRange(min) && inRange(max) && min <= max) {
      targeting.age_min = min;
      targeting.age_max = max;
    }
    return;
  }
  const plus = /^(\d{2})\s*\+$/.exec(s);
  if (plus) {
    const min = Number(plus[1]);
    if (inRange(min)) { targeting.age_min = min; targeting.age_max = 65; }
    return;
  }
  const bare = /^(\d{2})$/.exec(s);
  if (bare) {
    const n = Number(bare[1]);
    if (inRange(n)) { targeting.age_min = n; targeting.age_max = n; }
  }
}

// "All" / "Male" / "Female" / "Male, Female" — the gender cell's rendering.
// Meta encodes 1 = male, 2 = female, and NO key at all = everyone.
function applyFbGender(targeting, draft) {
  const tokens = splitCommaDraft(draft).map((t) => t.toLowerCase());
  if (!tokens.length || tokens.includes('all')) { delete targeting.genders; return; }
  const codes = [];
  for (const t of tokens) {
    if (t.startsWith('m') || t === '1') codes.push(1);
    else if (t.startsWith('f') || t === '2') codes.push(2);
  }
  if (codes.length) targeting.genders = [...new Set(codes)];
}

// ── List ops ─────────────────────────────────────────────────────────
// Ops are { op: 'remove' | 'add' | 'edit', value, newValue? } after
// normalizeRowOps (which also accepts the legacy bare-label remove arrays).

// Match a list entry by the label the row rendered (strings and {id, name}
// objects both appear in a Facebook targeting list).
const entryLabel = (v) => (v && typeof v === 'object'
  ? String(v.name ?? v.key ?? v.id ?? '')
  : String(v ?? ''));

// Apply add / edit / remove to a list, preserving the SHAPE the list already
// uses: an object-shaped list keeps objects (so an entry's Meta id survives an
// unrelated edit), a string list stays strings.
function applyEntryOps(list, ops, { objectKey = 'name' } = {}) {
  let next = asArray(list).slice();
  const objectShaped = next.some((v) => v && typeof v === 'object');
  for (const op of ops) {
    if (op.op === 'remove') {
      next = next.filter((v) => entryLabel(v) !== op.value);
    } else if (op.op === 'add') {
      if (!nonEmpty(op.value)) continue;
      next.push(objectShaped ? { [objectKey]: op.value } : op.value);
    } else if (op.op === 'edit' && nonEmpty(op.newValue)) {
      const i = next.findIndex((v) => entryLabel(v) === op.value);
      if (i < 0) continue;
      const hit = next[i];
      // An object entry keeps its id and every other key — only the label
      // changes, because the id is what actually targets.
      if (hit && typeof hit === 'object') next[i] = { ...hit, [objectKey]: op.newValue };
      else next[i] = op.newValue;
    }
  }
  return next;
}

// Which geo container a typed value belongs in. Meta keys them by kind, and
// putting a DMA in `countries` targets nothing — so an add routes by what the
// text looks like, in the same vocabulary the cell renders:
//   'US' / 'GB'      → countries (a bare 2-letter code)
//   'DMA 501' / '501'→ geo_markets (Meta's key form is 'DMA:501')
//   anything else    → cities, which the launcher resolves by name
//                      (fb_targeting_lookup) at launch
function fbGeoAddTarget(value) {
  const s = String(value).trim();
  if (!s) return null;
  if (/^[A-Za-z]{2}$/.test(s)) return { key: 'countries', entry: s.toUpperCase() };
  const dma = /^(?:dma[\s:]*)?(\d{3})$/i.exec(s);
  if (dma) return { key: 'geo_markets', entry: { key: `DMA:${dma[1]}`, name: s } };
  return { key: 'cities', entry: { name: s } };
}

function applyFbGeoAdd(bag, value) {
  const target = fbGeoAddTarget(value);
  if (!target) return;
  if (!Array.isArray(bag[target.key])) bag[target.key] = [];
  bag[target.key].push(target.entry);
}

// A geo REMOVE has to find the label wherever it lives — the cell unions six
// containers, so filtering only `countries` would leave a deleted DMA on the
// plan while the grid showed it gone.
const GEO_CONTAINERS = ['countries', 'country_groups', 'regions', 'cities', 'geo_markets', 'electoral_districts'];
function applyFbGeoRemove(bag, label) {
  for (const key of GEO_CONTAINERS) {
    if (Array.isArray(bag[key])) bag[key] = bag[key].filter((v) => entryLabel(v) !== label);
  }
}

function applyFbGeoOps(targeting, fieldOps) {
  for (const [field, containerKey] of [
    ['geoInclude', 'geo_locations'],
    ['geoExclude', 'excluded_geo_locations'],
  ]) {
    const ops = fieldOps[field] || [];
    if (!ops.length) continue;
    const bag = geoBag(targeting, containerKey);
    for (const op of ops) {
      if (op.op === 'remove') applyFbGeoRemove(bag, op.value);
      else if (op.op === 'add') applyFbGeoAdd(bag, op.value);
      else if (op.op === 'edit' && nonEmpty(op.newValue)) {
        // Edit = remove(old) + add(new): the old label can live in any
        // container, and the new value re-routes by kind.
        applyFbGeoRemove(bag, op.value);
        applyFbGeoAdd(bag, op.newValue);
      }
    }
  }
  // Zip codes live in the SAME geo_locations bag but have their own cell, so
  // they get their own ops rather than riding geoInclude.
  const zipOps = fieldOps.zipcodes || [];
  if (zipOps.length) {
    const bag = geoBag(targeting, 'geo_locations');
    const key = Array.isArray(bag.zipcodes) && !Array.isArray(bag.zips) ? 'zipcodes' : 'zips';
    bag[key] = applyEntryOps(bag[key], zipOps, { objectKey: 'key' });
  }
}

// Placements: the cell unions publisher_platforms and the per-surface position
// lists, so an add routes by whether the text names a PLATFORM (Meta's four)
// or a position within one.
const FB_PUBLISHER_PLATFORMS = new Set(['facebook', 'instagram', 'messenger', 'audience_network', 'threads']);

function applyFbPlacementOps(targeting, ops) {
  for (const op of ops) {
    if (op.op === 'remove') {
      for (const key of ['publisher_platforms', 'facebook_positions', 'instagram_positions', 'messenger_positions', 'audience_network_positions']) {
        if (Array.isArray(targeting[key])) {
          targeting[key] = targeting[key].filter((v) => entryLabel(v) !== op.value);
        }
      }
      continue;
    }
    const value = op.op === 'edit' ? op.newValue : op.value;
    if (!nonEmpty(value)) continue;
    if (op.op === 'edit') {
      for (const key of ['publisher_platforms', 'facebook_positions', 'instagram_positions', 'messenger_positions', 'audience_network_positions']) {
        if (Array.isArray(targeting[key])) {
          targeting[key] = targeting[key].filter((v) => entryLabel(v) !== op.value);
        }
      }
    }
    const token = String(value).trim().toLowerCase().replace(/\s+/g, '_');
    const key = FB_PUBLISHER_PLATFORMS.has(token) ? 'publisher_platforms' : 'facebook_positions';
    if (!Array.isArray(targeting[key])) targeting[key] = [];
    if (!targeting[key].some((v) => entryLabel(v) === token)) targeting[key].push(token);
  }
}

// Per-creative primary text. The cell lists each creative's `ad_copy`, so an
// edit rewrites the creative whose copy matched. Adds and removes are OFF in
// FB_LIST_FIELDS (a creative is added by upload, not by typing copy), and are
// refused here too so the two can't drift.
function applyFbAdCopyOps(adSet, ops) {
  const creatives = fbCreativesOf(adSet);
  if (!creatives.length) return;
  for (const op of ops) {
    if (op.op !== 'edit' || !nonEmpty(op.newValue)) continue;
    const hit = creatives.find((c) => String(c?.ad_copy ?? '') === op.value);
    if (hit) hit.ad_copy = op.newValue;
  }
}

function applyFbListOps(targets, fieldOps, result) {
  const { adSet } = targets;
  const targetingFields = ['geoInclude', 'geoExclude', 'zipcodes', 'audiences', 'interests', 'placements', 'devices', 'languages'];
  const touchesTargeting = targetingFields.some((f) => fieldOps[f]?.length);
  if (touchesTargeting) {
    const targeting = writableTargeting(adSet, result);
    applyFbGeoOps(targeting, fieldOps);
    if (fieldOps.audiences?.length) {
      // Adds/edits go to custom_audiences (the launcher resolves a name-only
      // entry); a REMOVE also has to reach lookalikes, which the cell unions in.
      const removes = fieldOps.audiences.filter((o) => o.op === 'remove');
      if (removes.length && Array.isArray(targeting.lookalike_audiences)) {
        targeting.lookalike_audiences = applyEntryOps(targeting.lookalike_audiences, removes);
      }
      targeting.custom_audiences = applyEntryOps(targeting.custom_audiences, fieldOps.audiences);
    }
    if (fieldOps.interests?.length) {
      targeting.interests = applyEntryOps(targeting.interests, fieldOps.interests);
    }
    if (fieldOps.placements?.length) applyFbPlacementOps(targeting, fieldOps.placements);
    if (fieldOps.devices?.length) {
      const next = applyEntryOps(targeting.device_platforms, fieldOps.devices)
        .map((v) => (typeof v === 'string' ? v.toLowerCase() : v));
      if (next.length) targeting.device_platforms = next;
      else delete targeting.device_platforms;
    }
    if (fieldOps.languages?.length) {
      targeting.locales = applyEntryOps(targeting.locales, fieldOps.languages);
    }
  }
  if (fieldOps.adCopy?.length) applyFbAdCopyOps(adSet, fieldOps.adCopy);
}

// ── Entry point ──────────────────────────────────────────────────────

/**
 * Apply pending edits + list ops to an already-cloned Facebook launcher
 * result, in place.
 *
 * `listOpsOrDeletions` accepts BOTH shapes the grids produce:
 *   - list ops (current): { rowId: { field: [{ op, value, newValue? }] } }
 *   - legacy deletions:   { rowId: { field: [label] } }  (remove-only)
 *
 * `reviewRows` is used only to skip rowIds the current grid doesn't know
 * about, matching applyLauncherResultEdits' contract.
 */
export function applyFbEdits(edited, edits, listOpsOrDeletions, reviewRows = [], rowOps = []) {
  const known = reviewRows.length ? new Set(reviewRows.map((r) => r.rowId)) : null;

  const applyScalars = (rowId, fields) => {
    const targets = resolveTargets(edited, rowId);
    if (!targets) return;
    applyFbCampaignScoped(targets.campaign, fields, targets.adSetCount);
    applyFbAdSetFields(targets.adSet, fields);
    // Only touch targeting when a targeting cell was actually edited —
    // writableTargeting materialises an inline object, and doing that for every
    // budget edit would quietly detach ad sets from their shared profiles.
    if (['age', 'gender', 'languages', 'devices'].some((f) => fields[f] != null)) {
      applyFbTargetingFields(writableTargeting(targets.adSet, edited), fields);
    }
  };

  for (const [rowId, fields] of Object.entries(edits || {})) {
    if (known && !known.has(rowId)) continue;
    applyScalars(rowId, fields);
  }

  for (const [rowId, fields] of Object.entries(listOpsOrDeletions || {})) {
    const targets = resolveTargets(edited, rowId);
    if (!targets) continue;
    applyFbListOps(targets, normalizeRowOps(fields), edited);
  }

  // Row ops LAST, after every value edit has landed on the rows that existed
  // when the batch was staged. Facebook rows are addressed positionally
  // (`fb-${i}`) because the plan carries no pre-launch ids, so a delete
  // renumbers its siblings — resolving the value edits above first is what
  // keeps them pointing at the rows the user actually saw. Clone overrides then
  // go through the same field appliers as a typed edit.
  if (Array.isArray(rowOps) && rowOps.length) {
    const { created } = applyFbRowOps(edited, rowOps);
    for (const { rowId, overrides } of created) {
      if (!overrides || !Object.keys(overrides).length) continue;
      applyScalars(rowId, overrides);
    }
  }

  return edited;
}
